import React, { useState } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/toaster';

// Components
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import Students from './components/Students';
import StudentProfile from './components/StudentProfile';
import Teachers from './components/Teachers';
import TeacherProfile from './components/TeacherProfile';
import Classes from './components/Classes';
import ClassDetail from './components/ClassDetail';
import Attendance from './components/Attendance';
import Fees from './components/Fees';
import Library from './components/Library';
import Exams from './components/Exams';
import Timetable from './components/Timetable';
import CalendarEvents from './components/CalendarEvents';
import Announcements from './components/Announcements';
import Settings from './components/Settings';
import Schools from './components/Schools';
import TopNav from './components/TopNav';
import Profile from './components/Profile';
import Search from './components/Search';
import SsoCallback from './components/SsoCallback';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Layout Component with Sidebar + Top Navbar
const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  return (
    <div className="min-h-screen bg-slate-900">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="lg:ml-64 min-h-screen flex flex-col">
        <TopNav isSidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        <main className="flex-1">
          <div className="p-4 lg:p-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/sso" element={<SsoCallback />} />
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} 
      />
      
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout><Dashboard /></Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/schools"
        element={
          <ProtectedRoute>
            <Layout><Schools /></Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/students"
        element={
          <ProtectedRoute>
            <Layout><Students /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/students/:id/edit"
        element={
          <ProtectedRoute>
            <Layout><StudentProfile editMode /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/students/:id"
        element={
          <ProtectedRoute>
            <Layout><StudentProfile /></Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/teachers"
        element={
          <ProtectedRoute>
            <Layout><Teachers /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teachers/:id/edit"
        element={
          <ProtectedRoute>
            <Layout><TeacherProfile editMode /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teachers/:id"
        element={
          <ProtectedRoute>
            <Layout><TeacherProfile /></Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/classes/:id"
        element={
          <ProtectedRoute>
            <Layout><ClassDetail /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/classes"
        element={
          <ProtectedRoute>
            <Layout><Classes /></Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/attendance"
        element={
          <ProtectedRoute>
            <Layout><Attendance /></Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/timetable"
        element={
          <ProtectedRoute>
            <Layout><Timetable /></Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/exams"
        element={
          <ProtectedRoute>
            <Layout><Exams /></Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/fees"
        element={
          <ProtectedRoute>
            <Layout><Fees /></Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/library"
        element={
          <ProtectedRoute>
            <Layout><Library /></Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/events"
        element={
          <ProtectedRoute>
            <Layout><CalendarEvents /></Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/announcements"
        element={
          <ProtectedRoute>
            <Layout><Announcements /></Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout><Settings /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout><Profile /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/search"
        element={
          <ProtectedRoute>
            <Layout><Search /></Layout>
          </ProtectedRoute>
        }
      />
      
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
        <Toaster />
      </AuthProvider>
    </Router>
  );
}

export default App;
